import React, { useEffect } from 'react';
import { ResusProvider, useResusContext } from '../../../apps/Resus/ResusContext';
import DrugComponent from '../../../apps/Resus/Drug';
import DripComponent from '../../../apps/Resus/Drip';
import { evaluateMedicationPredicate } from '../../../apps/Resus/predicateUtils';
import type { MedicationRecord } from '../types';
import { SAMPLE_PRESETS, type SamplePresetKey } from '../types';

interface Props {
  medication: MedicationRecord;
  hospital: string;
  preset: SamplePresetKey;
  onPresetChange: (preset: SamplePresetKey) => void;
}

// Convert our admin-shape MedicationRecord into the runtime shape that
// Drug.tsx/Drip.tsx expects. Strips nulls (the runtime fields are optional, not nullable).
function toRuntimeShape(med: MedicationRecord): any {
  const out: any = {};
  for (const [k, v] of Object.entries(med)) {
    if (v === null) continue;
    out[k] = v;
  }
  if (med.kind === 'drug') {
    out.isDrip = false;
  } else {
    out.isDrip = true;
  }
  return out;
}

// Tiny harness component that lives inside ResusProvider. Sets the patient context
// based on the preset, then renders Drug or Drip with a predicate-evaluation wrapper.
const PreviewBody: React.FC<{ medication: MedicationRecord; preset: SamplePresetKey }> = ({ medication, preset }) => {
  const { setAge, setWeight } = useResusContext();
  const presetData = SAMPLE_PRESETS[preset];

  useEffect(() => {
    setAge(presetData.ageMonths);
    setWeight(presetData.weight);
  }, [preset, presetData.weight, presetData.ageMonths, setAge, setWeight]);

  const runtime = toRuntimeShape(medication);
  // Use a tiny synchronous t() — the consumer i18n key resolution would surface
  // missing-key strings in preview which would just confuse the admin.
  const t = (key: string, opts?: any) => {
    if (opts && typeof opts === 'object') {
      const filled = key.replace(/\{\{(\w+)\}\}/g, (_m, name) => String(opts[name] ?? ''));
      return filled;
    }
    return key;
  };
  const predicateResult = evaluateMedicationPredicate(runtime, presetData.ageMonths, presetData.weight, t);

  if (medication.kind === 'drug') {
    return <DrugComponent drug={runtime} predicateResult={predicateResult} />;
  }
  return <DripComponent drip={runtime} predicateResult={predicateResult} />;
};

const MedicationPreview: React.FC<Props> = ({ medication, hospital, preset, onPresetChange }) => {
  const presetEntries = Object.entries(SAMPLE_PRESETS) as [SamplePresetKey, typeof SAMPLE_PRESETS[SamplePresetKey]][];

  return (
    <div className="resus-admin__preview">
      <div className="resus-admin__preview__heading">
        <span>תצוגה מקדימה</span>
        <div className="resus-admin__preview__patient-toggle">
          {presetEntries.map(([key, def]) => (
            <button
              key={key}
              type="button"
              className={`resus-admin__chip${preset === key ? ' resus-admin__chip--active' : ''}`}
              onClick={() => onPresetChange(key)}
            >
              {def.label}
            </button>
          ))}
        </div>
      </div>
      <div className="resus-admin__preview__body">
        <ResusProvider hospital={hospital || 'emek'}>
          <PreviewBody medication={medication} preset={preset} />
        </ResusProvider>
      </div>
      <p className="resus-admin__field-hint" style={{ marginTop: 8, marginBottom: 0 }}>
        התצוגה מתעדכנת בזמן אמת לפי השדות שמולאו. אם התרופה תלויה במשקל/גיל, החליפו בין הילדים לדוגמה.
      </p>
    </div>
  );
};

export default MedicationPreview;
